import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Home, FolderKanban, Ticket, User, Moon, Sun, Menu, LogOut, HardHat, Bell, Tags, Shield, Settings, Languages } from 'lucide-react';
import { Badge } from './ui/badge';
import axiosInstance from '../lib/axios';

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [settings, setSettings] = React.useState({
    site_name: 'SiteControl',
    site_logo: null
  });

  // Helper function to translate notification titles and messages
  const translateNotification = (title, message) => {
    // Translate common notification titles
    let translatedTitle = title;
    if (title === "New Ticket Assigned" || title === "Ticket Assigned") {
      translatedTitle = t('notifications.ticketAssigned');
    } else if (title === "Ticket Status Updated") {
      translatedTitle = t('notifications.statusUpdated');
    } else if (title === "New comment on ticket") {
      translatedTitle = t('notifications.newComment');
    }

    // Translate message patterns
    let translatedMessage = message;
    if (message.includes("You have been assigned")) {
      translatedMessage = message.replace("You have been assigned to ticket:", t('notifications.assignedTo'));
      translatedMessage = translatedMessage.replace("You have been assigned ticket:", t('notifications.assignedTo'));
    } else if (message.includes("commented on ticket")) {
      const parts = message.split(" commented on ticket: ");
      if (parts.length === 2) {
        translatedMessage = `${parts[0]} ${t('notifications.commentedOn')} ${parts[1]}`;
      }
    } else if (message.includes("status changed to")) {
      const parts = message.match(/(.*) status changed to (.*)/);
      if (parts) {
        translatedMessage = `${parts[1]} ${t('notifications.statusChangedTo')} ${parts[2]}`;
      }
    }

    return { title: translatedTitle, message: translatedMessage };
  };

  React.useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axiosInstance.get('/notifications');
        setNotifications(response.data.filter(n => !n.read));
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axiosInstance.get('/settings');
        setSettings({
          site_name: response.data.site_name || 'SiteControl',
          site_logo: response.data.site_logo
        });
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const navigation = [
    { name: t('navigation.dashboard'), href: '/', icon: Home },
    { name: t('navigation.projects'), href: '/projects', icon: FolderKanban },
    { name: t('navigation.tickets'), href: '/tickets', icon: Ticket },
    { name: t('navigation.notifications'), href: '/notifications', icon: Bell },
  ];

  const adminNavigation = user?.role === 'admin' ? [
    { name: t('navigation.users'), href: '/admin/users', icon: User },
    { name: t('navigation.roles'), href: '/admin/user-roles', icon: Shield },
    { name: t('navigation.categories'), href: '/admin/categories', icon: Tags },
    { name: 'Project Permissions', href: '/admin/project-permissions', icon: Shield },
    { name: t('navigation.siteSettings'), href: '/admin/site-settings', icon: Settings },
  ] : [];

  const NavLinks = () => (
    <>
      {navigation.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.href;
        return (
          <Link
            key={item.name}
            to={item.href}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors touch-target ${
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
            data-testid={`nav-${item.name.toLowerCase()}`}
            onClick={() => setMobileOpen(false)}
          >
            <Icon className="w-5 h-5" />
            {item.name}
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow border-r border-border bg-card overflow-y-auto">
          <div className="flex items-center gap-3 p-6 border-b border-border">
            {settings.site_logo ? (
              <div className="w-20 h-20 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg p-2">
                <img 
                 src={  settings.site_logo
                            ? (settings.site_logo.startsWith('http') 
                                ? settings.site_logo 
                                : settings.site_logo) // <--- Đơn giản thế này thôi!
                                : '/logo.png' 
                      }
                  alt="Site Logo" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <div className="p-2 bg-primary rounded-lg">
                <HardHat className="w-6 h-6 text-primary-foreground" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-black">{settings.site_name}</h1>
              <p className="text-xs text-muted-foreground">{t('common.fieldManagement')}</p>
            </div>
          </div>
          <nav className="flex-1 p-4 space-y-2">
            <NavLinks />
            {adminNavigation.length > 0 && (
              <>
                <div className="pt-4 pb-2 px-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase">Administration</p>
                </div>
                {adminNavigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors touch-target ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      data-testid={`nav-${item.name.toLowerCase()}`}
                    >
                      <Icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                <span className="text-sm font-bold text-primary-foreground">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b border-border">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            {settings.site_logo ? (
              <div className="w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <img 
                  src={  settings.site_logo
                            ? (settings.site_logo.startsWith('http') 
                                ? settings.site_logo 
                                : settings.site_logo) // <--- Đơn giản thế này thôi!
                                : '/logo.png' 
                      }
                  alt="Site Logo" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <div className="p-2 bg-primary rounded-lg">
                <HardHat className="w-5 h-5 text-primary-foreground" />
              </div>
            )}
            <h1 className="text-lg font-black">{settings.site_name}</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Language Switcher - Mobile */}
            <div className="flex gap-1">
              <Button
                variant={i18n.language === 'en' ? 'default' : 'outline'}
                size="sm"
                onClick={() => i18n.changeLanguage('en')}
                className={`h-8 px-2 font-bold text-xs ${
                  i18n.language === 'en' 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'border-2 border-gray-300 hover:border-blue-400'
                }`}
              >
                EN
              </Button>
              <Button
                variant={i18n.language === 'zh-TW' ? 'default' : 'outline'}
                size="sm"
                onClick={() => i18n.changeLanguage('zh-TW')}
                className={`h-8 px-2 font-bold text-xs ${
                  i18n.language === 'zh-TW' 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'border-2 border-gray-300 hover:border-green-400'
                }`}
              >
                TW
              </Button>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
                  <Bell className="w-5 h-5" />
                  {notifications.length > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                      {notifications.length}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-background border-border">
                <DropdownMenuLabel>{t('navigation.notifications')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    {t('notifications.noNew')}
                  </div>
                ) : (
                  <>
                    {notifications.slice(0, 5).map((notif) => {
                      const { title, message } = translateNotification(notif.title, notif.message);
                      return (
                        <DropdownMenuItem 
                          key={notif.id} 
                          className="flex-col items-start p-3 cursor-pointer hover:bg-accent"
                          onClick={() => {
                            if (notif.ticket_id) {
                              window.location.href = `/tickets/${notif.ticket_id}`;
                            }
                          }}
                        >
                          <p className="font-semibold">{title}</p>
                          <p className="text-sm text-muted-foreground">{message}</p>
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/notifications" className="w-full text-center text-primary">
                        {t('notifications.viewAll')}
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-border">
                    <div className="flex items-center gap-3">
                      {settings.site_logo ? (
                        <div className="w-20 h-20 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg p-2">
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${settings.site_logo}`} 
                            alt="Site Logo" 
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="p-2 bg-primary rounded-lg">
                          <HardHat className="w-6 h-6 text-primary-foreground" />
                        </div>
                      )}
                      <div>
                        <h1 className="text-xl font-black">{settings.site_name}</h1>
                        <p className="text-xs text-muted-foreground">Field Management</p>
                      </div>
                    </div>
                  </div>
                  <nav className="flex-1 p-4 space-y-2">
                    <NavLinks />
                    {adminNavigation.length > 0 && (
                      <>
                        <div className="pt-4 pb-2 px-4">
                          <p className="text-xs font-semibold text-muted-foreground uppercase">Administration</p>
                        </div>
                        {adminNavigation.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.href;
                          return (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors touch-target ${
                                isActive
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                              }`}
                              onClick={() => setMobileOpen(false)}
                            >
                              <Icon className="w-5 h-5" />
                              {item.name}
                            </Link>
                          );
                        })}
                      </>
                    )}
                  </nav>
                  <div className="p-4 border-t border-border space-y-2">
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      <User className="w-5 h-5" />
                      <span>Profile</span>
                    </Link>
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={logout}
                      data-testid="logout-button"
                    >
                      <LogOut className="w-5 h-5 mr-3" />
                      Logout
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block md:pl-64">
        <div className="sticky top-0 z-10 flex items-center justify-between h-16 px-6 bg-card border-b border-border">
          <div className="flex-1"></div>
          <div className="flex items-center gap-2">
            {/* Language Switcher - New Design */}
            <div className="flex gap-1">
              <Button
                variant={i18n.language === 'en' ? 'default' : 'outline'}
                size="sm"
                onClick={() => i18n.changeLanguage('en')}
                className={`h-8 px-3 font-bold ${
                  i18n.language === 'en' 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'border-2 border-gray-300 hover:border-blue-400'
                }`}
              >
                EN
              </Button>
              <Button
                variant={i18n.language === 'zh-TW' ? 'default' : 'outline'}
                size="sm"
                onClick={() => i18n.changeLanguage('zh-TW')}
                className={`h-8 px-3 font-bold ${
                  i18n.language === 'zh-TW' 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'border-2 border-gray-300 hover:border-green-400'
                }`}
              >
                TW
              </Button>
            </div>
            
            {/* Theme Switcher */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
                  <Bell className="w-5 h-5" />
                  {notifications.length > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                      {notifications.length}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-background border-border">
                <DropdownMenuLabel>{t('navigation.notifications')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    {t('notifications.noNew')}
                  </div>
                ) : (
                  <>
                    {notifications.slice(0, 5).map((notif) => {
                      const { title, message } = translateNotification(notif.title, notif.message);
                      return (
                        <DropdownMenuItem 
                          key={notif.id} 
                          className="flex-col items-start p-3 cursor-pointer hover:bg-accent"
                          onClick={() => {
                            if (notif.ticket_id) {
                              window.location.href = `/tickets/${notif.ticket_id}`;
                            }
                          }}
                        >
                          <p className="font-semibold">{title}</p>
                          <p className="text-sm text-muted-foreground">{message}</p>
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/notifications" className="w-full text-center text-primary">
                        {t('notifications.viewAll')}
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-sm font-bold text-primary-foreground">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden lg:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-background border-border">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="md:pl-64 pt-16 md:pt-0 pb-20 md:pb-0">
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border">
        <nav className="flex items-center justify-around p-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors touch-target ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
                data-testid={`mobile-nav-${item.name.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{item.name}</span>
              </Link>
            );
          })}
          <Link
            to="/profile"
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors touch-target ${
              location.pathname === '/profile' ? 'text-primary' : 'text-muted-foreground'
            }`}
            data-testid="mobile-nav-profile"
          >
            <User className="w-5 h-5" />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </nav>
      </div>
    </div>
  );
};

export default Layout;